import React, { Suspense } from 'react';
import './scss/index.scss';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { createStore, applyMiddleware, compose } from 'redux'; // Added 'compose'
import thunk from 'redux-thunk';
import promiseMiddleware from 'redux-promise-middleware';
import { ThemeProvider } from '@mui/material/styles';
import logger from 'redux-logger';
import { I18nextProvider } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import { AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js';
import { getAuthority, loginRequest, msalConfig } from './authConfig';
import App from './App';
import reportWebVitals from './reportWebVitals';
import reducer from './reducers';
import './tailwind.css';
import LandingPageSpinner from './Components/common/Skeletons/LandingPageSpinner';
import Context from './context/context';
import { THEME } from './utils/constants';
import { reactPlugin } from './services/ApplicationInsightsService';
import { ConvertProvider } from './context/ConvertProvider';
import configureI18n from './i18n';
import { logAppError, logAuthStep, logBackendStep } from './utils/logActions';
import { ACCESS_TOKEN_NAME } from './utils/enums';
import LoadingWithSpinner from './Components/common/LoadingWithSpinner/LoadingWithSpinner';
import { backendAuth } from './Api';
import { fetchUserData } from './actions/user';

// Set viewport height variable and keep it updated on resize
const setVh = () => {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
};
setVh();
window.addEventListener('resize', setVh);

// Inject initial loader directly into DOM (removed once react mounts)
const loader = document.createElement('div');
loader.id = 'initial-loader';
loader.style.position = 'fixed';
loader.style.top = '0';
loader.style.left = '0';
loader.style.width = '100vw';
loader.style.height = '100vh';
loader.style.display = 'flex';
loader.style.alignItems = 'center';
loader.style.justifyContent = 'center';
loader.style.backgroundColor = '#fff';
loader.innerHTML = `
  <div style="text-align:center;">
    <svg width="50" height="50" viewBox="0 0 100 100">
      <circle cx="50" cy="50" r="35" stroke="#1976d2" stroke-width="10"
        fill="none" stroke-dasharray="164.9336" stroke-dashoffset="123.7">
        <animateTransform attributeName="transform" type="rotate"
          from="0 50 50" to="360 50 50" dur="1s" repeatCount="indefinite"/>
      </circle>
    </svg>
    <p style="font-family:sans-serif;color:#1976d2;">Loading...</p>
  </div>
`;
document.body.appendChild(loader);

// ------------------ Redux / i18n / store ------------------

// UPDATED: Remove redux-devtools-extension, use built-in support
const composeEnhancers =
  (typeof window !== 'undefined' &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) ||
  compose;

const middlewares = [thunk, promiseMiddleware];
if (process.env.NODE_ENV === 'development') {
  middlewares.push(logger);
}

const store = createStore(
  reducer,
  composeEnhancers(applyMiddleware(...middlewares))
);

const i18nextConfig = { language: null };
const i18n = configureI18n({
  i18nextConfig,
  redux: {
    lookupRedux() {},
    cacheUserLanguageRedux() {}
  }
});

// ------------------ Parse incoming query params ------------------
const searchParams = new URLSearchParams(window.location.search);
const idTokenHint = searchParams.get('id_token_hint');

const authority = getAuthority(idTokenHint);
const msalInstance = new PublicClientApplication(msalConfig(authority));

// ------------------ Backend Authorization ------------------
const safeSetItem = (k, v) => {
  try {
    if (v !== undefined) localStorage.setItem(k, String(v));
  } catch (e) {
    console.warn('localStorage set failed', e);
  }
};

const callBackendAuthorization = async (accessToken) => {
  if (!accessToken) return null;
  try {
    logBackendStep('Authorization_Start', { timestamp: Date.now() });
    safeSetItem(ACCESS_TOKEN_NAME, accessToken);
    const authResponse = await backendAuth();
    logBackendStep('Authorization_Response', {
      status: authResponse?.status,
      user: authResponse?.data?.Data?.Id
    });

    if (authResponse?.status === 200 && authResponse?.data?.Data) {
      const { FirstName, LastName, Id, IsFirstLogin } = authResponse.data.Data;
      safeSetItem('AirswiftFirstName', FirstName);
      safeSetItem('AirswiftLastName', LastName);
      safeSetItem('UserId', Id);
      store.dispatch(fetchUserData(Id));
      logBackendStep('Authorization_Success', { userId: Id });
      return { Id, IsFirstLogin };
    } else {
      logBackendStep('Authorization_Non200', { status: authResponse?.status });
      return null;
    }
  } catch (error) {
    logAppError(error, 'BackendAuthorization');
    return null;
  }
};

// ------------------ Startup flow ------------------
(async function bootstrap() {
  try {
    let authAttempted = false;

    const authPromise = (async () => {
      try {
        const redirectResponse = await msalInstance.handleRedirectPromise();

        if (redirectResponse) {
          logAuthStep('Handle_Redirect_Promise response exist', {
            response: redactForLogs(redirectResponse)
          });
          msalInstance.setActiveAccount(redirectResponse.account);
          if (redirectResponse.accessToken) {
            safeSetItem(ACCESS_TOKEN_NAME, redirectResponse.accessToken);
            await callBackendAuthorization(redirectResponse.accessToken);
          } else {
            try {
              const account = redirectResponse.account;
              const tokenResp = await msalInstance.acquireTokenSilent({
                ...loginRequest,
                account
              });
              safeSetItem(ACCESS_TOKEN_NAME, tokenResp.accessToken);
              await callBackendAuthorization(tokenResp.accessToken);
            } catch (e) {
              logAppError(e, 'acquireTokenSilent after redirect failed');
            }
          }
        } else {
          const accounts = msalInstance.getAllAccounts();
          logAuthStep('Handle_Redirect_Promise getting account', {
            accountsCount: accounts?.length || 0
          });

          if (accounts && accounts.length > 0) {
            const activeAccount = msalInstance.getActiveAccount() || accounts[0];
            if (activeAccount) msalInstance.setActiveAccount(activeAccount);

            try {
              const silentResp = await msalInstance.acquireTokenSilent({
                ...loginRequest,
                account: activeAccount
              });
              safeSetItem(ACCESS_TOKEN_NAME, silentResp.accessToken);
              await callBackendAuthorization(silentResp.accessToken);
            } catch (acquireErr) {
              logAppError(acquireErr, 'silent token failed, will attempt ssoSilent if possible');

              try {
                const ssoParams = {
                  ...loginRequest,
                  loginHint: activeAccount?.username
                };

                const ssoResp = await msalInstance.ssoSilent(ssoParams);
                safeSetItem(ACCESS_TOKEN_NAME, ssoResp.accessToken);
                await callBackendAuthorization(ssoResp.accessToken);
                logAuthStep('SSO token acquired via ssoSilent');
              } catch (ssoErr) {
                logAppError(ssoErr, 'SSO silent failed, redirecting to login');
                const requiresAuth = window.location.pathname.startsWith('/dashboard') ||
                                   window.location.pathname === '/';
                if (requiresAuth) {
                  msalInstance.loginRedirect(loginRequest).catch((e) => {
                    logAppError(e, 'loginRedirect failed');
                  });
                  return;
                } else {
                  logAuthStep('Not redirecting to login: page not protected');
                }
              }
            }
          } else {
            const isProtectedRoute = window.location.pathname.startsWith('/dashboard');
            if (isProtectedRoute) {
              logAuthStep('No account in cache, redirecting to login');
              await msalInstance.loginRedirect(loginRequest);
              return;
            } else {
              logAuthStep('No account and not protected route - proceeding unauthenticated');
            }
          }
        }
      } catch (authError) {
        console.error('Auth flow error:', authError);
      } finally {
        authAttempted = true;
      }
    })();

    // Mount React immediately
    const container = document.getElementById('root');
    const root = ReactDOM.createRoot(container);

    root.render(
      <React.StrictMode>
        <AppInsightsErrorBoundary
          onError={(e) => {
            console.log('AppInsightsErrorBoundary caught an error:', e);
            logAppError(e, 'AppInsightsErrorBoundary');
          }}
          appInsights={reactPlugin}
        >
          <MsalProvider instance={msalInstance}>
            <ThemeProvider theme={THEME}>
              <Provider store={store}>
                <I18nextProvider i18n={i18n}>
                  <HelmetProvider>
                    <Context>
                      <ConvertProvider>
                        <Suspense fallback={<LoadingWithSpinner />}>
                          <App msalInstance={msalInstance} />
                        </Suspense>
                      </ConvertProvider>
                    </Context>
                  </HelmetProvider>
                </I18nextProvider>
              </Provider>
            </ThemeProvider>
          </MsalProvider>
        </AppInsightsErrorBoundary>
      </React.StrictMode>
    );

    // Remove initial loader after React mounts
    requestAnimationFrame(() => {
      const loaderElement = document.getElementById('initial-loader');
      if (loaderElement) {
        loaderElement.style.opacity = '0';
        loaderElement.style.transition = 'opacity 0.3s';
        setTimeout(() => loaderElement.remove(), 300);
      }
    });

    // Wait for auth in background
    await authPromise;

  } catch (error) {
    console.error('bootstrap failed: ', error);
    logAppError(error, 'bootstrap failed');
    try {
      await msalInstance.loginRedirect(loginRequest);
    } catch (e) {
      logAppError(e, 'fallback loginRedirect after bootstrap failure');
    }
  }
})();

function redactForLogs(obj) {
  if (!obj) return obj;
  const copy = { ...obj };
  if (copy.accessToken) copy.accessToken = '[REDACTED]';
  if (copy.idToken) copy.idToken = '[REDACTED]';
  return copy;
}

reportWebVitals();